import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  Business as BusinessIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../../utils/swalConfig';

const EditClient = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    id_bu: '',
    client: ''
  });
  const [buData, setBuData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingBu, setLoadingBu] = useState(true);
  const [loadingData, setLoadingData] = useState(true);
  const navigate = useNavigate();

  // Fetch Business Units
  const fetchBusinessUnits = async () => {
    try {
      setLoadingBu(true);
      const response = await fetch('https://api-sso-v2.eurekagroup.id/bu', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      setBuData(result);
      console.log('Business units loaded:', result);
    } catch (error) {
      console.error('Error fetching business units:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: 'Gagal memuat data business unit'
      });
    } finally {
      setLoadingBu(false);
    }
  };

  // Fetch Client Data
  const fetchClientData = async () => {
    try {
      setLoadingData(true);
      const response = await fetch(`https://api-sso-v2.eurekagroup.id/api/clients/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      const clientData = result.data || result;
      setFormData({
        id_bu: clientData.id_bu.toString(),
        client: clientData.clients
      });
      console.log('Client data loaded:', clientData);
    } catch (error) {
      console.error('Error fetching client data:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal memuat data client: ${error.message}`
      });
      navigate('/sso/client');
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchBusinessUnits();
    fetchClientData();
  }, [id]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validation
    if (!formData.id_bu || !formData.client) {
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: 'Semua field harus diisi'
      });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`https://api-sso-v2.eurekagroup.id/api/clients/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          id_bu: parseInt(formData.id_bu),
          clients: formData.client
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Client updated:', result);
      
      SwalConfig.success(
        'Berhasil!',
        'Client SSO berhasil diperbarui',
        2000
      );
      
      navigate('/sso/client');
    } catch (error) {
      console.error('Error updating client:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal memperbarui client: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };


  if (loadingData) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 400,
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          Memuat data client...
        </Typography>
      </Box>
    );
  }

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <BusinessIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Edit Client SSO
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Perbarui data client SSO
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sso/client')}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Kembali
            </Button>
          }
          sx={{ pb: 2 }}
        />
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel>Business Unit</InputLabel>
                  <Select
                    name="id_bu"
                    value={formData.id_bu}
                    onChange={handleInputChange}
                    label="Business Unit"
                    disabled={loadingBu}
                  >
                    {buData.map((bu) => (
                      <MenuItem key={bu._id} value={bu.bu_code}>
                        {bu.bu_name} ({bu.bu_code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="client"
                  label="Nama Client"
                  value={formData.client}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  placeholder="Masukkan nama client (contoh: ERP System, Dashboard, dll)"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/sso/client')}
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Batal
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={loading}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    {loading ? 'Memperbarui...' : 'Perbarui Client'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditClient;
